import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../models/user';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UserRoles } from 'src/app/models/user-roles';
import { CommonConfig } from 'src/app/constants/common-config';
import { DataTableRequest } from 'src/app/models/datatable-request';
import { GeneralResponse } from 'src/app/models/general-response';
import { RiwayatProfile } from '../../models/riwayat-profile.t';
import { PaginatedRiwayatProfile } from '../../models/paginated-riwayat-profile.t';
import { LampiranRiwayat } from '../../models/lampiran-riwayat.t';
import { Statistik } from 'src/app/models/statistik.t';
import { ProfileDisplaysList } from '../../models/profile-display.t';
import { RiwayatProgressDetail } from '../../models/riwayat-progress-detail.t';
import { RiwayatProgress } from 'src/app/models/riwayat-progress.t';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  readonly path = '/profile';
  currentProfile$: BehaviorSubject<User> = new BehaviorSubject({ username: '', userRoles: <UserRoles[]>[] });

  constructor(private http: HttpClient) { }

  loadUser(userId: string) {
    if (this.currentProfile$.value.id !== userId) {
      this.getUser(userId).subscribe((profile) => {
        this.currentProfile$.next(profile);
      });
    }
  }

  getUser(userId: string): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}${this.path}/${userId}`);
  }

  getProfileDisplay(userId: string): Observable<ProfileDisplaysList> {
    return this.http.get<ProfileDisplaysList>(`${environment.apiUrl}${this.path}/display/${userId}`);
  }

  getDatas(payload: DataTableRequest): Observable<PaginatedRiwayatProfile> {
    const params = new HttpParams().append('searchTerm', payload.searchTerm);

    return this.http.get<PaginatedRiwayatProfile>(`${environment.apiUrl}${this.path}/list/${payload.userId}/${payload.typeId}/${payload.page}/${payload.dataPerPage}`,
      {
        ...CommonConfig.HTTP_OPTIONS,
        params: params,
      }
    );
  }

  getData(id: string): Observable<RiwayatProfile> {
    return this.http.get<RiwayatProfile>(`${environment.apiUrl}${this.path}/detail/${id}`,
      {
        ...CommonConfig.HTTP_OPTIONS,
      }
    );
  }

  update(section: string, data: User): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}${this.path}/save/${section}`, data, {
      ...CommonConfig.HTTP_OPTIONS,
    });
  }

  save(data: RiwayatProfile): Observable<RiwayatProfile> {
    return this.http.post(`${environment.apiUrl}${this.path}/riwayat/save`, data, {
      ...CommonConfig.HTTP_OPTIONS,
    });
  }

  delete(data: RiwayatProfile): Observable<GeneralResponse> {
    return this.http.delete(`${environment.apiUrl}${this.path}/${data.id}`);
  }

  deleteLampiran(data: LampiranRiwayat): Observable<GeneralResponse> {
    return this.http.delete(`${environment.apiUrl}${this.path}/lampiran/${data.id}`);
  }

  deleteProgress(data: RiwayatProgressDetail): Observable<GeneralResponse> {
    return this.http.post(`${environment.apiUrl}${this.path}/remove-progress/${data.id}`, data.id);
  }

  completeStep(data: RiwayatProgress): Observable<GeneralResponse> {
    return this.http.post(`${environment.apiUrl}${this.path}/complete-step/${data.id}`, data.id);
  }

  upload(
    referenceId: string,
    files: File[],
    typeId: string,
  ): Observable<GeneralResponse> {
    const formData: FormData = new FormData();

    formData.append('referenceId', referenceId);
    formData.append('typeId', typeId);
    for (let i = 0; i < files.length; i++) {
      formData.append('file', files[i]);
    }
    formData.append('title', '');

    return this.http.post<GeneralResponse>(
      `${environment.apiUrl}/upload/`,
      formData,
    );
  }

  getStatistik(userId: string): Observable<Statistik> {
    return this.http.get<Statistik>(`${environment.apiUrl}${this.path}/statistik/${userId}`);
  }
}
